import type { ContainerLevelHistory } from "@ots-frontend/ots-data";
import { useMemo, useState } from "react";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
} from "../components/OtsDataTable";
import { OtsDateTime } from "../components/OtsDateTime";
import { OtsSegmentedControl } from "../components/OtsSegmentedControl";
import { type OtsTimeSeriesMarker, OtsTimeSeriesChart } from "../components/OtsTimeSeriesChart";
import { containerLevelSeries, containerLevelSourceLabel, type ContainerLevelMeasure } from "./container-level-series";

export type ContainerLevelChartProps = {
  levels: ContainerLevelHistory[];
  /** Moments to mark, e.g. when the ticket was written. */
  markers?: OtsTimeSeriesMarker[];
  timeZone?: string;
  /** The unit volumes are in, when the readings don't say. */
  volumeUnit?: string;
  size?: "compact" | "default";
};

const formatQuantity = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * A tank's readings over time, by source (SCADA, driver gauging, field gauging), as a chart or a table, in volume
 * or level. The table holds the same readings for anyone who can't read the chart.
 */
export const ContainerLevelChart = ({ levels, markers, timeZone, volumeUnit, size = "default" }: ContainerLevelChartProps) => {
  const [view, setView] = useState<"chart" | "table">("chart");
  const [measure, setMeasure] = useState<ContainerLevelMeasure>("volume");
  const series = useMemo(() => containerLevelSeries(levels, measure), [levels, measure]);
  const unit = measure === "volume" ? levels.find((reading) => reading.volumeUom)?.volumeUom ?? volumeUnit : levels.find((reading) => reading.levelUom)?.levelUom;
  const valueLabel = `${measure === "volume" ? "Volume" : "Level"}${unit ? ` (${unit})` : ""}`;
  const rows = useMemo(() => [...levels].sort((left, right) => right.createDttm - left.createDttm), [levels]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <OtsSegmentedControl<"chart" | "table"> ariaLabel="Show" size={size} value={view} onChange={setView}
          segments={[{ id: "chart", label: "Chart" }, { id: "table", label: "Table" }]}/>
        <OtsSegmentedControl<ContainerLevelMeasure> ariaLabel="Measure" size={size} value={measure} onChange={setMeasure}
          segments={[{ id: "volume", label: "Volume" }, { id: "level", label: "Level" }]}/>
      </div>
      {!levels.length ? (
        <p className="py-10 text-center text-sm text-[var(--ots-text-muted)]">No readings were recorded for this tank in this time.</p>
      ) : view === "chart" ? (
        series.length
          ? <OtsTimeSeriesChart series={series} ariaLabel={`Tank ${measure} over time`} valueLabel={valueLabel} formatValue={formatQuantity} markers={markers}/>
          : <p className="py-10 text-center text-sm text-[var(--ots-text-muted)]">No readings have a {measure}.</p>
      ) : (
        <OtsDataTable aria-label="Tank readings" density={size === "compact" ? "compact" : "default"}>
          <OtsDataTableHead>
            <OtsDataTableRow>
              <OtsDataTableHeaderCell>Time</OtsDataTableHeaderCell>
              <OtsDataTableHeaderCell>Source</OtsDataTableHeaderCell>
              <OtsDataTableHeaderCell className="text-right">Volume</OtsDataTableHeaderCell>
              <OtsDataTableHeaderCell className="text-right">Level</OtsDataTableHeaderCell>
            </OtsDataTableRow>
          </OtsDataTableHead>
          <OtsDataTableBody>
            {rows.map((reading) => (
              <OtsDataTableRow key={reading.id}>
                <OtsDataTableCell><OtsDateTime value={reading.createDttm} format="short" timeZone={timeZone}/></OtsDataTableCell>
                <OtsDataTableCell>{containerLevelSourceLabel(reading.type)}</OtsDataTableCell>
                <OtsDataTableCell className="text-right tabular-nums">
                  {reading.volume != null ? `${formatQuantity(reading.volume)} ${reading.volumeUom ?? volumeUnit ?? ""}`.trim() : "—"}
                </OtsDataTableCell>
                <OtsDataTableCell className="text-right tabular-nums">
                  {reading.level != null ? `${formatQuantity(reading.level)} ${reading.levelUom ?? ""}`.trim() : "—"}
                </OtsDataTableCell>
              </OtsDataTableRow>
            ))}
          </OtsDataTableBody>
        </OtsDataTable>
      )}
    </div>
  );
};
